import { Injectable } from '@angular/core';
import { Observable, zip } from 'rxjs';
import { StructureContractService } from '../interfaces/structure-contract.service';
import JSZip from 'jszip';
import { SubjectModelDbService } from '../database/subject-model-db.service';
import { switchMap } from 'rxjs/operators';
import { SubjectLocation } from '../../_objects/subject-model';
import { TitleDbService } from '../database/title-db.service';
import { InspectionDbService } from '../database/inspection-db.service';
import { TitleSchema } from '../../_offline/models/title.schema';
import { InspectionSchema } from '../../_offline/models/inspection.schema';
import { SubjectDbService } from '../database/subject-db.service';
import { OfflineUtilsService } from '../../_offline/services/offline-utils.service';
import { UtilsService } from '../_utils.service';
import { MarksStateService } from "../mark-state/marks-state.service";
import { MarkItem } from '../../_objects/mark';
import { FILE_SYSTEM } from '../../_offline/electron.constants';

@Injectable({
  providedIn: 'root'
})
export class StructureOfflineService implements StructureContractService {

  readonly MARK_FILE_NAME = 'MARK_SET.dat';
  readonly TITLE_FILE_NAME = 'TITLE.dat';

  private folderIndex = 1;

  constructor(
    private inspectionDbService: InspectionDbService,
    private subjectDbService: SubjectDbService,
    private subjectModelDbService: SubjectModelDbService,
    private titleDbService: TitleDbService,
    private utilsService: UtilsService,
    private offlineUtils: OfflineUtilsService,
    private marksStateService: MarksStateService
  ) { }

  getStructureZip(subjectModelId?: string, inspectionId?: string, withPreset?: boolean): Observable<Blob> {
    this.folderIndex = 1;
    if (subjectModelId) {
      return this.getWithSubjectModelId(subjectModelId, withPreset);
    }

    return this.getWithInspectionId(inspectionId, withPreset);
  }

  getWithInspectionId(inspectionId: string, withPreset?: boolean) {
    let inspection: InspectionSchema;
    let marks: MarkItem[];
    let title: TitleSchema;

    return zip(
      this.inspectionDbService.getInspection(inspectionId, true),
      this.marksStateService.markItems$,
      this.titleDbService.getAll()
    )
      .pipe(switchMap(values => {
        inspection = values[0];
        marks = values[1];
        title = values[2];

        return this.subjectDbService.getOne(inspection.subjectId)
          .pipe(switchMap(subject => this.subjectModelDbService.getOne(subject.subjectModelId)));
      }))
      .pipe(switchMap(async subjectModel => {
        const subjectModelLocation = subjectModel.subjectModelLocations.find(_ => _.version === inspection.locationVersion);
        const locations = JSON.parse(subjectModelLocation.locations) as SubjectLocation[];

        const zipFile = new JSZip();
        const inspFolder = zipFile.folder(this.utilsService.trimAllSpace(inspection.title));

        // 部位の終端フォルダを追加
        locations.forEach(loc => {
          this.addLocationFolder(loc, inspFolder);
        });

        if (withPreset) {
          const presetFolder = zipFile.folder(FILE_SYSTEM.PATH.PRESET);
          presetFolder.file(this.MARK_FILE_NAME, this.offlineUtils.generateMarksText(marks));
          presetFolder.file(this.TITLE_FILE_NAME, this.offlineUtils.generateTitlesText(title));
        }

        // ZIPファイルのblobデータ生成
        const blob = await zipFile.generateAsync<'blob'>({
          type: 'blob',
          platform: 'UNIX',
          compression: 'DEFLATE',
          compressionOptions: { level: 9 }
        });

        return blob;
      }));
  }

  getWithSubjectModelId(subjectModelId: string, withPreset?: boolean) {
    return zip(
      this.subjectModelDbService.getOne(subjectModelId),
      this.marksStateService.markItems$,
      this.titleDbService.getAll()
    )
      .pipe(switchMap(async values => {
        const subjectModel = values[0];
        const marks = values[1];
        const title = values[2];

        const newestLocations = subjectModel.subjectModelLocations.find(_ => _.isNewestVersion);
        const locations = JSON.parse(newestLocations.locations) as SubjectLocation[];

        const zipFile = new JSZip();
        const inspFolder = zipFile.folder(this.utilsService.trimAllSpace(subjectModel.name));

        // 部位の終端フォルダを追加
        locations.forEach(loc => {
          this.addLocationFolder(loc, inspFolder);
        });

        if (withPreset) {
          const presetFolder = zipFile.folder(FILE_SYSTEM.PATH.PRESET);
          presetFolder.file(this.MARK_FILE_NAME, this.offlineUtils.generateMarksText(marks));
          presetFolder.file(this.TITLE_FILE_NAME, this.offlineUtils.generateTitlesText(title));
        }

        // ZIPファイルのblobデータ生成
        const blob = await zipFile.generateAsync<'blob'>({
          type: 'blob',
          platform: 'UNIX',
          compression: 'DEFLATE',
          compressionOptions: { level: 9 }
        });

        return blob;
      }));
  }

  addLocationFolder(location: SubjectLocation, baseFolder: JSZip) {

    if (location.children.length > 0) {
      location.children.forEach(child => {
        this.addLocationFolder(child, baseFolder);
      });
    } else {
      if (location.isDataPlate) {
        const folderName = '00_' + location.identifier;
        baseFolder.folder(folderName);
      } else {
        const folderName = ('00' + this.folderIndex).slice(-2) + '_' + location.identifier;
        baseFolder.folder(folderName);
        this.folderIndex++;
      }
    }

    return baseFolder;
  }
}
